import React, { useState, useEffect, useRef } from 'react';
import { ICON } from '../icons/iconIds';
import { selkirkIconImgHtml } from '../icons/getSelkirkIconUrl';
import IconText from './icons/IconText';
import SelkirkIcon from './icons/SelkirkIcon';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Layout from './layout/Layout';
import { claimsService } from '../services/claimsService';
import Spinner from './ui/Spinner';
import Card from './ui/Card';
import Button from './ui/Button';
import './ClaimManagement.css';

// Fix Leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Selkirk: time-to-expiry buckets (remaining_days) for map + stats
const SELKIRK_D1Y = 365;
const SELKIRK_D2Y = 730;

/** @returns {'expired'|'within30'|'within90'|'within1y'|'within2y'|'beyond2y'} */
function getSelkirkExpiryBucket(remainingDays, status) {
  const d = parseInt(remainingDays, 10);
  const days = Number.isFinite(d) ? d : 0;
  const st = status && String(status).toLowerCase();
  const isActive = st === 'active';
  if (!isActive || days <= 0) return 'expired';
  if (days <= 30) return 'within30';
  if (days <= 90) return 'within90';
  if (days <= SELKIRK_D1Y) return 'within1y';
  if (days <= SELKIRK_D2Y) return 'within2y';
  return 'beyond2y';
}

function getSelkirkLabelAndColor(remainingDays, status) {
  const b = getSelkirkExpiryBucket(remainingDays, status);
  const labels = {
    expired: { label: 'Expired / Inactive', color: '#6b7280' },
    within30: { label: 'Expires within 30 days', color: '#dc2626' },
    within90: { label: 'Expires within 90 days', color: '#ea580c' },
    within1y: { label: 'Expires within 1 year', color: '#ca8a04' },
    within2y: { label: 'Expires within 2 years', color: '#15803d' },
    beyond2y: { label: 'Expires further in the future', color: '#1d4ed8' }
  };
  return labels[b];
}

const ClaimManagement = ({ user }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const selkirkLayerRef = useRef(null);
  const nonSelkirkLayerRef = useRef(null);
  const layerControlRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selkirkData, setSelkirkData] = useState(null);
  const [nonSelkirkData, setNonSelkirkData] = useState(null);
  const [selkirkStats, setSelkirkStats] = useState(null);
  const [nonSelkirkStats, setNonSelkirkStats] = useState(null);
  const [showSelkirk, setShowSelkirk] = useState(true);
  const [showNonSelkirk, setShowNonSelkirk] = useState(true);

  // Initialize map on mount
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    }
    
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Load claims data when map is ready
  useEffect(() => {
    if (mapInstanceRef.current) {
      loadAllClaimsData();
    }
  }, [mapInstanceRef.current]);

  // Toggle layer visibility
  useEffect(() => {
    if (mapInstanceRef.current) {
      // Process Non-Selkirk first
      if (nonSelkirkLayerRef.current) {
        if (showNonSelkirk) {
          mapInstanceRef.current.addLayer(nonSelkirkLayerRef.current);
        } else {
          mapInstanceRef.current.removeLayer(nonSelkirkLayerRef.current);
        }
      }
      // Process Selkirk second and bring to front to ensure it's always on top
      if (selkirkLayerRef.current) {
        if (showSelkirk) {
          mapInstanceRef.current.addLayer(selkirkLayerRef.current);
          // Ensure Selkirk is always on top
          selkirkLayerRef.current.bringToFront();
        } else {
          mapInstanceRef.current.removeLayer(selkirkLayerRef.current);
        }
      }
    }
  }, [showSelkirk, showNonSelkirk]);

  const initializeMap = () => {
    console.log('🗺️ Initializing Claim Management map...');
    
    // Create Leaflet map centered on claims area
    const map = L.map(mapRef.current, {
      center: [62.5, -137.2],
      zoom: 11,
      zoomControl: true,
      attributionControl: true
    });

    // Define base layers
    const streetMap = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18
    });

    const satelliteMap = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: '© Esri, Maxar, Earthstar Geographics, and the GIS User Community',
      maxZoom: 18
    });

    // Add default base layer (satellite)
    satelliteMap.addTo(map);

    // Create layer control for base layers
    const baseLayers = {
      "Street Map": streetMap,
      "Satellite": satelliteMap
    };

    const control = L.control.layers(baseLayers, {}, {
      position: 'topleft',
      collapsed: false
    });
    control.addTo(map);
    layerControlRef.current = control;

    // Add zoom to claims extent button
    const zoomToExtentControl = L.Control.extend({
      options: { position: 'topright' },
      onAdd: function() {
        const div = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        div.innerHTML = '<button style="background: #28a745; color: white; border: none; padding: 5px 10px; border-radius: 3px; cursor: pointer; font-size: 12px; font-weight: 500;">Zoom to Claims</button>';
        div.onclick = () => {
          map.fitBounds([
            [62.231286, -137.98022],
            [62.812662, -136.509917]
          ], { padding: [20, 20] });
        };
        return div;
      }
    });
    new zoomToExtentControl().addTo(map);

    // Add scale control
    L.control.scale({
      position: 'bottomleft',
      imperial: false,
      metric: true
    }).addTo(map);

    // Add legend control
    addLegendControl(map);

    // Set initial view to claims extent
    map.fitBounds([
      [62.231286, -137.98022],
      [62.812662, -136.509917]
    ], { padding: [20, 20] });

    mapInstanceRef.current = map;
    console.log('✅ Map initialized');
  };

  const addLegendControl = (map) => {
    const legendControl = L.Control.extend({
      options: { position: 'bottomright' },
      onAdd: function() {
        const div = L.DomUtil.create('div', 'legend');
        div.innerHTML = `
          <div style="background: #1f2937; padding: 12px; border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.4); font-family: inherit; font-size: 12px; max-width: 220px; border: 1px solid #374151;">
            <h4 style="margin: 0 0 10px 0; color: #f9fafb; text-align: center; font-weight: 600;">Selkirk Claims</h4>
            
            <div style="border-top: 1px solid #4b5563; padding-top: 8px;">
              <p style="margin: 4px 0;"><span style="display: inline-block; width: 16px; height: 16px; background: #dc2626; border: 2px solid #dc2626; border-radius: 3px; margin-right: 6px; vertical-align: middle; opacity: 0.65;"></span><span style="font-size: 10px; color: #e5e7eb;">Within 30 days</span></p>
              <p style="margin: 4px 0;"><span style="display: inline-block; width: 16px; height: 16px; background: #ea580c; border: 2px solid #ea580c; border-radius: 3px; margin-right: 6px; vertical-align: middle; opacity: 0.65;"></span><span style="font-size: 10px; color: #e5e7eb;">Within 90 days</span></p>
              <p style="margin: 4px 0;"><span style="display: inline-block; width: 16px; height: 16px; background: #ca8a04; border: 2px solid #ca8a04; border-radius: 3px; margin-right: 6px; vertical-align: middle; opacity: 0.65;"></span><span style="font-size: 10px; color: #e5e7eb;">Within 1 year</span></p>
              <p style="margin: 4px 0;"><span style="display: inline-block; width: 16px; height: 16px; background: #15803d; border: 2px solid #15803d; border-radius: 3px; margin-right: 6px; vertical-align: middle; opacity: 0.65;"></span><span style="font-size: 10px; color: #e5e7eb;">Within 2 years</span></p>
              <p style="margin: 4px 0;"><span style="display: inline-block; width: 16px; height: 16px; background: #1d4ed8; border: 2px solid #1d4ed8; border-radius: 3px; margin-right: 6px; vertical-align: middle; opacity: 0.65;"></span><span style="font-size: 10px; color: #e5e7eb;">Further out</span></p>
              <p style="margin: 4px 0;"><span style="display: inline-block; width: 16px; height: 16px; background: #6b7280; border: 2px solid #6b7280; border-radius: 3px; margin-right: 6px; vertical-align: middle; opacity: 0.45; border-style: dashed;"></span><span style="font-size: 10px; color: #e5e7eb;">Expired / inactive</span></p>
            </div>
            
            <!-- Non-Selkirk - De-emphasized -->
            <div style="border-top: 1px solid #4b5563; padding-top: 6px; margin-top: 8px;">
              <p style="margin: 0 0 4px 0; font-size: 9px; color: #6b7280; font-style: italic;">Other Claims (grayscale)</p>
            </div>
            
            <p style="margin: 8px 0 0 0; font-style: italic; color: #9ca3af; font-size: 9px; text-align: center; border-top: 1px solid #4b5563; padding-top: 8px;">
              ${selkirkIconImgHtml(ICON.uiSecurityLock, 12)} Azure AD Secured<br/>
              ${selkirkIconImgHtml(ICON.uiLocationPin, 12)} Click claim for details
            </p>
          </div>
        `;
        return div;
      }
    });
    new legendControl().addTo(map);
  };

  const loadAllClaimsData = async () => {
    try {
      console.log('📡 Loading all claims data...');
      setLoading(true);
      setError(null);

      // Load both Selkirk and Non-Selkirk claims in parallel
      const [selkirkGeoJSON, nonSelkirkGeoJSON] = await Promise.all([
        claimsService.getAllClaims({ type: 'selkirk' }),
        claimsService.getAllClaims({ type: 'nonselkirk' })
      ]);

      console.log('✅ Selkirk claims loaded:', selkirkGeoJSON.features?.length || 0);
      console.log('✅ Non-Selkirk claims loaded:', nonSelkirkGeoJSON.features?.length || 0);

      // Validate geometries
      validateGeometries(selkirkGeoJSON, 'Selkirk');
      validateGeometries(nonSelkirkGeoJSON, 'Non-Selkirk');

      setSelkirkData(selkirkGeoJSON);
      setNonSelkirkData(nonSelkirkGeoJSON);

      // Calculate stats
      calculateSelkirkStats(selkirkGeoJSON);
      calculateNonSelkirkStats(nonSelkirkGeoJSON);

      // Render layers (Non-Selkirk first, so Selkirk appears on top)
      renderNonSelkirkLayer(nonSelkirkGeoJSON);
      renderSelkirkLayer(selkirkGeoJSON);

      setLoading(false);
    } catch (error) {
      console.error('❌ Error loading claims:', error);
      setError(`Failed to load claims data: ${error.message}`);
      setLoading(false);
    }
  };

  const validateGeometries = (geojsonData, type) => {
    if (!geojsonData.features || geojsonData.features.length === 0) {
      console.warn(`⚠️ No ${type} claims returned from database`);
      return;
    }

    const hasGeometries = geojsonData.features.some(f => f.geometry && f.geometry.coordinates);
    if (!hasGeometries) {
      console.error(`⚠️ No valid geometries found in ${type} features`);
      throw new Error(`${type} claims data missing geometry coordinates.`);
    }
    console.log(`✅ Valid geometries found for ${type} claims`);
  };

  const calculateSelkirkStats = (geojsonData) => {
    const empty = {
      total: 0,
      expired: 0,
      within30: 0,
      within90: 0,
      within1y: 0,
      within2y: 0,
      beyond2y: 0
    };
    if (!geojsonData.features || geojsonData.features.length === 0) {
      setSelkirkStats(empty);
      return;
    }

    const stats = {
      total: geojsonData.features.length,
      expired: 0,
      within30: 0,
      within90: 0,
      within1y: 0,
      within2y: 0,
      beyond2y: 0
    };

    geojsonData.features.forEach(feature => {
      const props = feature.properties;
      const status = props.status;
      const bucket = getSelkirkExpiryBucket(props.remaining_days, status);
      switch (bucket) {
        case 'expired':
          stats.expired++;
          break;
        case 'within30':
          stats.within30++;
          break;
        case 'within90':
          stats.within90++;
          break;
        case 'within1y':
          stats.within1y++;
          break;
        case 'within2y':
          stats.within2y++;
          break;
        case 'beyond2y':
          stats.beyond2y++;
          break;
        default:
          break;
      }
    });

    setSelkirkStats(stats);
  };

  const calculateNonSelkirkStats = (geojsonData) => {
    if (!geojsonData.features || geojsonData.features.length === 0) {
      setNonSelkirkStats({ total: 0, active: 0, notActive: 0 });
      return;
    }

    const stats = {
      total: geojsonData.features.length,
      active: 0,
      notActive: 0
    };

    geojsonData.features.forEach(feature => {
      const status = feature.properties.status?.toLowerCase();
      if (status === 'active') {
        stats.active++;
      } else {
        stats.notActive++;
      }
    });

    setNonSelkirkStats(stats);
  };

  const renderSelkirkLayer = (geojsonData) => {
    if (!mapInstanceRef.current) return;

    // Remove existing layer
    if (selkirkLayerRef.current) {
      mapInstanceRef.current.removeLayer(selkirkLayerRef.current);
    }

    // Create GeoJSON layer with Selkirk styling
    const layer = L.geoJSON(geojsonData, {
      style: (feature) => getSelkirkClaimStyle(feature),
      onEachFeature: (feature, layer) => {
        const props = feature.properties;
        
        // Create hover tooltip
        const tooltipContent = createSelkirkTooltip(props);
        layer.bindTooltip(tooltipContent, {
          permanent: false,
          direction: 'top',
          className: 'claim-tooltip',
          opacity: 0.95,
          offset: [0, -10]
        });

        // Add click handler
        layer.on('click', (e) => {
          showClaimPopup(feature, e.latlng, 'selkirk');
        });

        // Hover effects
        layer.on('mouseover', function() {
          this.setStyle({ weight: 3, fillOpacity: 0.5 });
        });

        layer.on('mouseout', function() {
          selkirkLayerRef.current.resetStyle(this);
        });
      }
    });

    if (showSelkirk) {
      layer.addTo(mapInstanceRef.current);
      // Ensure Selkirk is always on top
      layer.bringToFront();
    }
    selkirkLayerRef.current = layer;
    console.log('✅ Selkirk claims layer rendered');
  };

  const renderNonSelkirkLayer = (geojsonData) => {
    if (!mapInstanceRef.current) return;

    // Remove existing layer
    if (nonSelkirkLayerRef.current) {
      mapInstanceRef.current.removeLayer(nonSelkirkLayerRef.current);
    }

    // Create GeoJSON layer with grayscale styling
    const layer = L.geoJSON(geojsonData, {
      style: (feature) => getNonSelkirkClaimStyle(feature),
      onEachFeature: (feature, layer) => {
        const props = feature.properties;
        
        // Create hover tooltip
        const tooltipContent = createNonSelkirkTooltip(props);
        layer.bindTooltip(tooltipContent, {
          permanent: false,
          direction: 'top',
          className: 'claim-tooltip',
          opacity: 0.95,
          offset: [0, -10]
        });

        // Add click handler
        layer.on('click', (e) => {
          showClaimPopup(feature, e.latlng, 'nonselkirk');
        });

        // Hover effects
        layer.on('mouseover', function() {
          this.setStyle({ weight: 3, fillOpacity: 0.5 });
        });

        layer.on('mouseout', function() {
          nonSelkirkLayerRef.current.resetStyle(this);
        });
      }
    });

    if (showNonSelkirk) {
      layer.addTo(mapInstanceRef.current);
    }
    nonSelkirkLayerRef.current = layer;
    console.log('✅ Non-Selkirk claims layer rendered');
  };

  const getSelkirkClaimStyle = (feature) => {
    const props = feature.properties;
    const bucket = getSelkirkExpiryBucket(props.remaining_days, props.status);

    const byBucket = {
      expired: { color: '#6b7280', fill: '#6b7280', fillOpacity: 0.22, weight: 1.5 },
      within30: { color: '#dc2626', fill: '#dc2626', fillOpacity: 0.42, weight: 2.5 },
      within90: { color: '#ea580c', fill: '#ea580c', fillOpacity: 0.38, weight: 2.2 },
      within1y: { color: '#ca8a04', fill: '#ca8a04', fillOpacity: 0.36, weight: 2 },
      within2y: { color: '#15803d', fill: '#15803d', fillOpacity: 0.34, weight: 2 },
      beyond2y: { color: '#1d4ed8', fill: '#1d4ed8', fillOpacity: 0.32, weight: 1.8 }
    };

    const s = byBucket[bucket] || byBucket.beyond2y;
    const isExpired = bucket === 'expired';

    return {
      color: s.color,
      weight: s.weight,
      opacity: 0.88,
      fillColor: s.fill,
      fillOpacity: s.fillOpacity,
      dashArray: isExpired ? '6, 5' : null
    };
  };

  const getNonSelkirkClaimStyle = (feature) => {
    const props = feature.properties;
    const status = props.status?.toLowerCase();

    // Grayscale styling for non-Selkirk claims
    let color = '#9ca3af'; // Medium gray border
    let fillColor = '#d1d5db'; // Light gray fill
    let fillOpacity = 0.3;
    let weight = 1.5;

    if (status !== 'active') {
      // Darker gray for inactive
      color = '#4b5563';
      fillColor = '#6b7280';
      fillOpacity = 0.25;
      weight = 1;
    }

    return {
      color: color,
      weight: weight,
      opacity: 0.7,
      fillColor: fillColor,
      fillOpacity: fillOpacity,
      dashArray: status !== 'active' ? '4, 4' : null
    };
  };

  const createSelkirkTooltip = (props) => {
    const remainingDays = parseInt(props.remaining_days) || 0;
    const { label: urgencyText, color: statusColor } = getSelkirkLabelAndColor(
      remainingDays,
      props.status
    );

    return `
      <div style="min-width: 200px; font-family: inherit;">
        <div style="background: #1f2937; padding: 8px 12px; border-bottom: 2px solid ${statusColor}; margin: -8px -10px 8px -10px;">
          <div style="font-weight: 700; font-size: 13px; color: #f9fafb; margin-bottom: 2px;">
            ${props.claim_name || props.claim_num || 'Unknown Claim'}
          </div>
          <div style="font-size: 10px; color: #9ca3af;">
            ${props.owner || 'No owner listed'}
          </div>
        </div>
        
        <div style="font-size: 11px; line-height: 1.4;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #9ca3af;">Status:</span>
            <span style="color: ${statusColor}; font-weight: 600;">${urgencyText}</span>
          </div>
          
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #9ca3af;">Days Remaining:</span>
            <span style="color: ${statusColor}; font-weight: 700; font-size: 12px;">${remainingDays}</span>
          </div>
          
          ${props.expiry_dat ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #9ca3af;">Expires:</span>
            <span style="color: #e5e7eb; font-weight: 500;">${props.expiry_dat}</span>
          </div>
          ` : ''}
          
          ${props.grant_num ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #9ca3af;">Grant #:</span>
            <span style="color: #e5e7eb;">${props.grant_num}</span>
          </div>
          ` : ''}
        </div>
        
        <div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid #374151; font-size: 9px; color: #6b7280; font-style: italic; text-align: center;">
          Click for full details
        </div>
      </div>
    `;
  };

  const createNonSelkirkTooltip = (props) => {
    const status = props.status?.toLowerCase();
    const statusColor = status === 'active' ? '#9ca3af' : '#6b7280';

    return `
      <div style="min-width: 200px; font-family: inherit;">
        <div style="background: #1f2937; padding: 8px 12px; border-bottom: 2px solid ${statusColor}; margin: -8px -10px 8px -10px;">
          <div style="font-weight: 700; font-size: 13px; color: #f9fafb; margin-bottom: 2px;">
            ${props.claim_name || props.claim_num || 'Unknown Claim'}
          </div>
          <div style="font-size: 10px; color: #9ca3af;">
            ${props.owner || 'No owner listed'}
          </div>
        </div>
        
        <div style="font-size: 11px; line-height: 1.4;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #9ca3af;">Status:</span>
            <span style="color: ${statusColor}; font-weight: 600;">${props.status || 'Unknown'}</span>
          </div>
          
          ${props.expiry_dat ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #9ca3af;">Expiry Date:</span>
            <span style="color: #e5e7eb; font-weight: 500;">${props.expiry_dat}</span>
          </div>
          ` : ''}
          
          ${props.grant_num ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #9ca3af;">Grant #:</span>
            <span style="color: #e5e7eb;">${props.grant_num}</span>
          </div>
          ` : ''}
          
          ${props.tenure ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #9ca3af;">Tenure:</span>
            <span style="color: #e5e7eb;">${props.tenure}</span>
          </div>
          ` : ''}
        </div>
        
        <div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid #374151; font-size: 9px; color: #6b7280; font-style: italic; text-align: center;">
          Click for full details
        </div>
      </div>
    `;
  };

  const showClaimPopup = (feature, latlng, type) => {
    const props = feature.properties;
    
    let popupContent = `
      <div style="max-width: 350px; font-family: inherit; background: #ffffff;">
        <h4 style="margin: 0 0 12px 0; text-align: center; padding: 10px; background: ${type === 'selkirk' ? '#3498db' : '#6b7280'}; color: #ffffff; border-radius: 5px; font-weight: 600;">
          ${selkirkIconImgHtml(ICON.appClaimManagement, 14)} ${props.claim_name || props.claim_num || 'Quartz Claim'}
        </h4>
    `;

    // Ownership Section
    if (props.owner || props.grant_num) {
      popupContent += `
        <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; margin-bottom: 8px; border: 1px solid #dee2e6;">
          <h5 style="margin: 0 0 6px 0; color: #1f2937; font-size: 12px; font-weight: 600;">${selkirkIconImgHtml(ICON.uiClipboardList, 12)} OWNERSHIP</h5>
      `;
      if (props.owner) popupContent += `<p style="margin: 3px 0; font-size: 11px; color: #374151;"><strong style="color: #1f2937;">Owner:</strong> ${props.owner}</p>`;
      if (props.grant_num) popupContent += `<p style="margin: 3px 0; font-size: 11px; color: #374151;"><strong style="color: #1f2937;">Grant #:</strong> ${props.grant_num}</p>`;
      popupContent += `</div>`;
    }

    // Status Section
    if (props.status || props.tenure) {
      popupContent += `
        <div style="background: #d1fae5; padding: 10px; border-radius: 5px; margin-bottom: 8px; border: 1px solid #a7f3d0;">
          <h5 style="margin: 0 0 6px 0; color: #065f46; font-size: 12px; font-weight: 600;">${selkirkIconImgHtml(ICON.uiChartAnalytics, 12)} STATUS</h5>
      `;
      if (props.status) {
        const statusColor = props.status.toLowerCase() === 'active' ? '#27ae60' : '#e74c3c';
        popupContent += `<p style="margin: 3px 0; font-size: 11px; color: #374151;"><strong style="color: #1f2937;">Status:</strong> <span style="color: ${statusColor}; font-weight: 600;">${props.status}</span></p>`;
      }
      if (props.tenure) popupContent += `<p style="margin: 3px 0; font-size: 11px; color: #374151;"><strong style="color: #1f2937;">Tenure:</strong> ${props.tenure}</p>`;
      popupContent += `</div>`;
    }

    // Dates Section
    if (props.stake_date || props.expiry_dat || props.remaining_days !== undefined) {
      popupContent += `
        <div style="background: #fef3c7; padding: 10px; border-radius: 5px; margin-bottom: 8px; border: 1px solid #fde68a;">
          <h5 style="margin: 0 0 6px 0; color: #78350f; font-size: 12px; font-weight: 600;">${selkirkIconImgHtml(ICON.uiCalendarDate, 12)} DATES</h5>
      `;
      if (props.stake_date) popupContent += `<p style="margin: 3px 0; font-size: 11px; color: #374151;"><strong style="color: #1f2937;">Staked:</strong> ${props.stake_date}</p>`;
      if (props.expiry_dat) popupContent += `<p style="margin: 3px 0; font-size: 11px; color: #374151;"><strong style="color: #1f2937;">Expires:</strong> ${props.expiry_dat}</p>`;
      if (props.remaining_days !== undefined && type === 'selkirk') {
        const { color: dayColor } = getSelkirkLabelAndColor(props.remaining_days, props.status);
        popupContent += `<p style="margin: 3px 0; font-size: 11px; color: #374151;"><strong style="color: #1f2937;">Days Left:</strong> <span style="color: ${dayColor}; font-weight: 600;">${props.remaining_days}</span></p>`;
      }
      popupContent += `</div>`;
    }

    // Location Section
    if (props.district) {
      popupContent += `
        <div style="background: #dbeafe; padding: 10px; border-radius: 5px; margin-bottom: 8px; border: 1px solid #bfdbfe;">
          <h5 style="margin: 0 0 6px 0; color: #1e40af; font-size: 12px; font-weight: 600;">${selkirkIconImgHtml(ICON.uiGlobeLocation, 12)} LOCATION</h5>
          <p style="margin: 3px 0; font-size: 11px; color: #374151;"><strong style="color: #1f2937;">District:</strong> ${props.district}</p>
        </div>
      `;
    }

    // Footer
    popupContent += `
        <div style="border-top: 1px solid #e5e7eb; padding-top: 10px; text-align: center; background: #f9fafb; margin: 0 -12px -12px -12px; padding: 10px 12px; border-radius: 0 0 5px 5px;">
          <p style="margin: 0 0 4px 0; font-size: 10px; color: #4b5563; font-weight: 500;">
            ${selkirkIconImgHtml(ICON.uiLocationPin, 10)} ${latlng.lat.toFixed(6)}°N, ${Math.abs(latlng.lng).toFixed(6)}°W
          </p>
          <p style="margin: 0; font-size: 9px; color: #3b82f6; font-weight: 600;">
            ${selkirkIconImgHtml(ICON.uiSuccess, 10)} ${type === 'selkirk' ? 'Selkirk' : 'Non-Selkirk'} Claim
          </p>
        </div>
      </div>
    `;

    L.popup({
      maxWidth: 380,
      className: 'claim-popup'
    })
      .setLatLng(latlng)
      .setContent(popupContent)
      .openOn(mapInstanceRef.current);
  };

  const refreshData = () => {
    loadAllClaimsData();
  };

  return (
    <Layout user={user} currentRoute="/claim-management">
      <div className="claim-management">
        {/* Compact Header */}
        <div className="claim-header-compact">
          <IconText icon={ICON.appClaimManagement} as="h1" className="page-title-compact">
            Claim Management
          </IconText>
          
          {/* Layer Toggles */}
          <div className="layer-toggles-compact">
            <Button 
              variant={showSelkirk ? 'primary' : 'ghost'} 
              size="sm" 
              onClick={() => setShowSelkirk(!showSelkirk)}
            >
              <SelkirkIcon name={showSelkirk ? ICON.uiCheckOn : ICON.uiRadioOff} size={14} decorative /> Selkirk Claims
            </Button>
            <Button 
              variant={showNonSelkirk ? 'primary' : 'ghost'} 
              size="sm" 
              onClick={() => setShowNonSelkirk(!showNonSelkirk)}
            >
              <SelkirkIcon name={showNonSelkirk ? ICON.uiCheckOn : ICON.uiRadioOff} size={14} decorative /> Non-Selkirk
            </Button>
          </div>
        </div>

        {/* Stats Bar - Selkirk Only */}
        {!loading && selkirkStats && (
          <div className="stats-bar selkirk-stats">
            <div className="stat-card">
              <span className="stat-value">{selkirkStats.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-card">
              <span className="stat-value" style={{ color: '#dc2626' }}>{selkirkStats.within30}</span>
              <span className="stat-label">≤30 days</span>
            </div>
            <div className="stat-card">
              <span className="stat-value" style={{ color: '#ea580c' }}>{selkirkStats.within90}</span>
              <span className="stat-label">31–90 days</span>
            </div>
            <div className="stat-card">
              <span className="stat-value" style={{ color: '#ca8a04' }}>{selkirkStats.within1y}</span>
              <span className="stat-label">91d–1 yr</span>
            </div>
            <div className="stat-card">
              <span className="stat-value" style={{ color: '#15803d' }}>{selkirkStats.within2y}</span>
              <span className="stat-label">1–2 years</span>
            </div>
            <div className="stat-card">
              <span className="stat-value" style={{ color: '#1d4ed8' }}>{selkirkStats.beyond2y}</span>
              <span className="stat-label">&gt;2 years</span>
            </div>
            <div className="stat-card">
              <span className="stat-value" style={{ color: '#6b7280' }}>{selkirkStats.expired}</span>
              <span className="stat-label">Expired / inactive</span>
            </div>
          </div>
        )}

        {/* Map Section */}
        <div className="map-section">
          {/* Loading Overlay */}
          {loading && (
            <div className="map-overlay">
              <Card variant="elevated" className="map-loading">
                <div className="loading-content">
                  <Spinner size="large" />
                  <h3>Loading Claims Data...</h3>
                  <p>Fetching Selkirk and Non-Selkirk claims from database</p>
                </div>
              </Card>
            </div>
          )}

          {/* Error Overlay */}
          {error && (
            <div className="map-overlay">
              <Card variant="elevated" className="map-error">
                <div className="error-content">
                  <IconText icon={ICON.uiWarning} as="h3">Error Loading Claims</IconText>
                  <p>{error}</p>
                  <Button variant="primary" onClick={refreshData}>
                    Retry
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {/* Map Container */}
          <div 
            ref={mapRef} 
            className="leaflet-map" 
            style={{ height: 'calc(100vh - 220px)', width: '100%' }}
          />
          
          {/* Map Footer */}
          {!loading && !error && (
            <div className="map-footer-info">
              <strong>Data Source:</strong> <span>PostgreSQL Database</span> | 
              <strong>Status:</strong> <span style={{ color: '#10b981' }}>● Online</span> | 
              <strong>Auth:</strong>{' '}
              <span style={{ color: '#3b82f6', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <SelkirkIcon name={ICON.uiSecurityLock} size={14} decorative /> Azure AD
              </span>{' '}
              | 
              <strong>Selkirk Claims:</strong> <span style={{ color: '#f59e0b' }}>
                {selkirkStats?.total || 0}
              </span>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ClaimManagement;
